import styles from "./ProtectPlusCompliance.module.scss";
import { ProtectPlusScheme } from "@/components/PostAddToBasketDrawer/scheme/schemeDecisioner.ts";
import React, { useEffect, useRef, useState } from "react";
import Tick from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/Tick24PxOutlined";
import Info from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/Info24PxOutlined";

import {
  Services,
  TransformedExtendedWarrantyMulti,
} from "@/types/serviceTypes";
import OpenInNew24PxOutlined from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/OpenInNew24PxOutlined";
import { HorizontalRule } from "@/components";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import {
  PrimaryButton,
  SecondaryButton,
  UnderlinedButton,
} from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import CheckboxWithContainer from "@JohnLewisPartnership/jl-design-system/dist/components/checkbox/CheckboxWithContainer";
import { Stepper } from "@JohnLewisPartnership/jl-design-system/dist/components/stepper";
import { ProtectPlusSvg } from "@/components/PostAddToBasketDrawer/components/ProtectPlusCompliance/ProtectPlusSvg.tsx";
import { replaceVariables } from "@/components/PostAddToBasketDrawer/utils.ts";

type ProtectPlusComplianceProps = {
  protectPlusScheme: ProtectPlusScheme;
  guarantees?: { title: string }[];
  services: Services;
  addProtectPlusClicked: (
    s: TransformedExtendedWarrantyMulti,
    q: number,
  ) => Promise<boolean>;
  closeDrawer: () => void;
  maxAllowedServices: number;
  handleStepperChange: (
    s: TransformedExtendedWarrantyMulti,
    q: number,
  ) => Promise<boolean>;
  hideFooter: boolean;
  isDrawerOpen: boolean;
};

export const ProtectPlusCompliance = ({
  protectPlusScheme,
  services,
  addProtectPlusClicked,
  closeDrawer,
  maxAllowedServices,
  handleStepperChange,
  hideFooter,
  isDrawerOpen,
}: ProtectPlusComplianceProps) => {
  const protectPlusService = services.filter(
    (service) => service.serviceType === "ExtendedWarranty",
  )[0] as TransformedExtendedWarrantyMulti;
  const duration = protectPlusService?.duration;
  const marketingMessage = replaceVariables(
    protectPlusScheme.schemeMarketingMessage || "",
    {
      VAR_YEARS: protectPlusService?.duration,
      VAR_PRICE: "",
    },
  );
  const complaintDetails =
    "Full details on how to complain can be found in the";

  const elementCheckBox = useRef<HTMLDivElement>(null);
  const topOfComponent = useRef<HTMLDivElement>(null);

  const [checked, setChecked] = useState(false);
  const [disableAddProtectPlusButton, setDisableAddProtectPlusButton] =
    useState(true);
  const [termsAgreed, setTermsAgreed] = useState(true);

  const [serviceQuantity, setServiceQuantity] = useState(
    protectPlusService && protectPlusService.quantity > 0
      ? protectPlusService.quantity
      : 1,
  );
  const [protectPlusAdded, setProtectPlusAdded] = useState(
    !!protectPlusService?.selected,
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setProtectPlusAdded(
      protectPlusService?.selected ? protectPlusService.selected : false,
    );
  }, [protectPlusService]);

  useEffect(() => {
    if (isDrawerOpen) {
      topOfComponent.current?.parentElement?.scrollTo({ top: 0 });
    }
  }, [isDrawerOpen]);

  const insuranceProductInformationDocument = (
    <a
      href={protectPlusScheme.insuranceProductInformationDocument}
      target="_blank"
      rel="noreferrer"
    >
      {"Insurance Product Information Document"}
    </a>
  );

  const termsAndConditions = (
    <a
      href={protectPlusScheme.termsAndConditions}
      target="_blank"
      rel="noreferrer"
    >
      {"Terms and Conditions"}
    </a>
  );

  const handleScroll = () => {
    elementCheckBox.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleCheck = () => {
    setChecked((old) => !old);
    setDisableAddProtectPlusButton(checked);
    setTermsAgreed(!checked);
  };

  if (!protectPlusService) {
    return null;
  }

  function handleQuantityChange(quantity: number) {
    if (!protectPlusService) {
      return;
    }

    if (protectPlusAdded) {
      setIsLoading(true);

      handleStepperChange(protectPlusService, quantity).then((isSuccess) => {
        if (isSuccess) {
          setServiceQuantity(quantity);
        }

        setIsLoading(false);
      });
    } else {
      setServiceQuantity(quantity);
    }
  }

  return (
    <div
      className={styles.protectPlusComplianceWrapper}
      data-testid="post-a2b-drawer-compliance"
      ref={topOfComponent}
    >
      <div className={styles.protectPlusCompliance}>
        <>
          <div className={styles.protectPlusCompliance__accidentalCover}>
            <div>
              Add {duration ? duration + " years" : ""} accidental damage cover?
            </div>
            <div
              className={
                styles.protectPlusCompliance__accidentalCover__standardGuarantee
              }
            >
              {marketingMessage}
            </div>
          </div>

          {protectPlusService && (
            <div>
              <div className={styles.protectPlusCompliance__service}>
                <p
                  className={styles.protectPlusCompliance__service__title}
                  data-testid="service-label_compliance"
                >
                  <span>
                    <ProtectPlusSvg />
                  </span>
                  <span
                    className={
                      styles.protectPlusCompliance__service__title__price
                    }
                  >
                    {protectPlusService.display}
                    {maxAllowedServices > 1 && !hideFooter && (
                      <span
                        className={
                          styles.protectPlusCompliance__service__title__per_policy
                        }
                      >
                        per policy
                      </span>
                    )}
                  </span>
                </p>
                {maxAllowedServices > 1 && !hideFooter && (
                  <Stepper
                    data-testid="compliance-protect-plus-stepper"
                    value={serviceQuantity}
                    inputId={"post-add-service-quantity"}
                    handleChange={handleQuantityChange}
                    inputName={"quantity-selector"}
                    min={1}
                    max={maxAllowedServices}
                  />
                )}
              </div>
              <div className={styles.protectPlusCompliance__whatsCovered}>
                <div>
                  <Tick />
                  {" What's covered"}
                </div>
                <ul>
                  {protectPlusScheme.whatsCovered?.map(
                    (whatsCovered, index) => (
                      <li key={`guarantee-${index}`}>
                        {whatsCovered.coverage}
                        {whatsCovered.details &&
                          whatsCovered.details.length > 0 && (
                            <ul className={styles.dash}>
                              {whatsCovered.details.map((detail) => (
                                <li key={detail}>{detail}</li>
                              ))}
                            </ul>
                          )}
                      </li>
                    ),
                  )}
                </ul>
              </div>

              <div className={styles.protectPlusCompliance__whatsNotCovered}>
                <div>
                  <Info />
                  {"What's not covered"}
                </div>
                <ul>
                  {protectPlusScheme.whatsNotCovered?.map(
                    (whatsNotCovered, index) => (
                      <li key={`no-guarantee-${index}`}>
                        {whatsNotCovered.exclusion}
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </div>
          )}
        </>
        <div></div>
        <div>
          <div
            data-testid="demands-and-needs"
            className={styles.protectPlusCompliance__demandsAndNeeds}
          >
            {protectPlusScheme.demandsAndNeedsStatement} See{" "}
            {insuranceProductInformationDocument}
            {"."}
            {" " + complaintDetails + " "} {termsAndConditions}
            <div data-testid="demands-and-needs-taxes">
              Price includes all applicable taxes.
            </div>
          </div>
        </div>

        <div className={styles.protectPlusCompliance__findOutMore}>
          <UnderlinedButton
            size="small"
            tag="button"
            type="button"
            onClick={() =>
              window.open(
                "https://www.johnlewis.com/our-services/protect-plus",
                "_blank",
              )
            }
          >
            Find out more about Protect Plus
          </UnderlinedButton>
          <OpenInNew24PxOutlined />
        </div>

        <div className={styles.protectPlusCompliance__providedBy}>
          Provided By Domestic &amp; General Insurance PLC
        </div>

        <HorizontalRule />
        <div>
          {!protectPlusAdded && !hideFooter && (
            <>
              <div
                className={
                  termsAgreed
                    ? styles.protectPlusCompliance__checkbox
                    : styles.protectPlusCompliance__checkboxContainer
                }
              >
                <CheckboxWithContainer
                  id={"protect-plus-service-checkbox"}
                  name={"Protect Plus"}
                  checked={checked}
                  isControlled={true}
                  onClick={handleCheck}
                  disabled={isLoading}
                  additionalInfo={
                    !termsAgreed && (
                      <p>
                        {
                          "Please confirm that you've read and understood the Information Document and Terms and Conditions"
                        }
                      </p>
                    )
                  }
                  label={
                    <p data-testid="service-label">
                      I can confirm I have read the{" "}
                      {insuranceProductInformationDocument} and{" "}
                      {termsAndConditions} and that this policy meets my demands
                      and needs
                    </p>
                  }
                  data-testid="post-a2b-drawer-protect-plus-compliance-checkbox"
                />
              </div>
              <div className={styles.scrollToBottom} ref={elementCheckBox}>
                Scroll To Bottom
              </div>
            </>
          )}
        </div>
      </div>

      {!hideFooter && (
        <div
          className={styles.protectPlusCompliance__footer}
          data-testid="post-a2b-drawer-compliance-footer"
        >
          {protectPlusAdded ? (
            <>
              <Message
                dataTestId="added-to-your-basket"
                title={
                  maxAllowedServices === 0
                    ? "Protect Plus selected"
                    : (serviceQuantity > 1 ? serviceQuantity + " x " : "") +
                      "Protect Plus added to your basket"
                }
                body={
                  maxAllowedServices === 0 ? (
                    <span>
                      Now add your item to basket and we&apos;ll include Protect
                      Plus with your order
                    </span>
                  ) : (
                    <span>
                      You have confirmed that you have read the{" "}
                      {insuranceProductInformationDocument} and{" "}
                      {termsAndConditions} and that this policy meets your
                      demands and needs
                    </span>
                  )
                }
                className={styles.protectPlusCompliance__footer__message}
                type="success"
              />

              <PrimaryButton
                className={styles.protectPlusCompliance__continueShoppingButton}
                onClick={closeDrawer}
              >
                Continue Shopping
              </PrimaryButton>
            </>
          ) : (
            <>
              {maxAllowedServices === 0 && !protectPlusAdded && (
                <Message
                  title={
                    "Protect Plus will be added once you have added your item to basket"
                  }
                  className={styles.protectPlusCompliance__footer__message}
                  type="information"
                />
              )}
              <PrimaryButton
                className={styles.protectPlusCompliance__addServiceButton}
                disabled={isLoading}
                onClick={async () => {
                  if (disableAddProtectPlusButton) {
                    setTermsAgreed(false);
                    handleScroll();
                  } else {
                    setIsLoading(true);
                    const result = await addProtectPlusClicked(
                      protectPlusService,
                      serviceQuantity,
                    );
                    if (result) {
                      setProtectPlusAdded(true);
                    }
                    setIsLoading(false);
                  }
                }}
              >
                Add Protect Plus
              </PrimaryButton>
              <SecondaryButton
                className={styles.protectPlusCompliance__skipServiceButton}
                onClick={closeDrawer}
              >
                Continue without Protect Plus
              </SecondaryButton>
            </>
          )}
        </div>
      )}
    </div>
  );
};
